import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Label, Button } from 'react-aria-components';
import clsx from 'clsx';
import { CustomCheckbox } from '@shared/components/CustomCheckbox/CustomCheckbox';
import { CustomTextInput } from '@shared/components/CustomTextInput/CustomTextInput';
import { CustomButton } from '@shared/components/CustomButton/CustomButton';
import styles from './CustomSelect.module.css';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface CustomSelectProps {
  /**
   * Select label
   */
  label?: string;
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Helper text to display below the select
   */
  helperText?: string;
  /**
   * Full width select
   */
  fullWidth?: boolean;
  /**
   * Options to display
   */
  options: SelectOption[];
  /**
   * Selected value(s)
   */
  value?: string | string[];
  /**
   * Change handler
   */
  onChange?: (value: string | string[]) => void;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Additional CSS class
   */
  className?: string;
  /**
   * Enable multi-select
   */
  multiSelect?: boolean;
  /**
   * Enable search functionality
   */
  searchable?: boolean;
  /**
   * Placeholder for search input
   */
  searchPlaceholder?: string;
  /**
   * Message to display when no options are found
   */
  noOptionsMessage?: string;
  /**
   * Enable option removal
   */
  removable?: boolean;
  /**
   * Callback when an option is removed
   */
  onRemove?: (value: string) => void;
  /**
   * Disabled state
   */
  disabled?: boolean;
  /**
   * Enable create new option functionality
   */
  creatable?: boolean;
  /**
   * Callback when creating a new option
   */
  onCreate?: (value: string, autoSelect?: boolean) => void;
  /**
   * Callback when options array changes
   */
  onOptionsChange?: (options: SelectOption[]) => void;
  /**
   * Placeholder for the create input
   */
  createPlaceholder?: string;
  /**
   * Button text for create action
   */
  createButtonText?: string;
  /**
   * Auto-select created value
   */
  autoSelectCreated?: boolean;
  /**
   * Maximum character length for create input
   */
  createMaxLength?: number;
  /**
   * Size of the select
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Custom border color (only applies to default state, not open/error)
   */
  borderColor?: string;
  /**
   * Remove border radius
   */
  hasNoRadius?: boolean;
  /**
   * Remove box shadow
   */
  hasNoBoxShadow?: boolean;
  /**
   * Enable clear button in trigger
   */
  clearable?: boolean;
  /**
   * Callback when selection is cleared
   */
  onClear?: () => void;
  /**
   * Sort options alphabetically by label (default: true)
   */
  sortOptions?: boolean;
}

/**
 * CustomSelect component - an enhanced select with search, multi-select, and removal capabilities
 */
export const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  error,
  helperText,
  fullWidth = false,
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className = '',
  multiSelect = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  noOptionsMessage = 'No options found',
  removable = false,
  onRemove,
  disabled = false,
  creatable = false,
  onCreate,
  onOptionsChange,
  createPlaceholder = 'Enter option name',
  createButtonText = 'Create',
  autoSelectCreated = false,
  createMaxLength,
  size = 'medium',
  borderColor,
  hasNoRadius = false,
  hasNoBoxShadow = false,
  clearable = false,
  onClear,
  sortOptions = true,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);
  const [createValue, setCreateValue] = useState('');
  const [createError, setCreateError] = useState('');
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const hasError = !!error;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Normalize value to array for easier handling
  const selectedValues = useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    const optionsWithLabel = options.filter((option) => option.label);
    const filtered = !searchQuery
      ? optionsWithLabel
      : optionsWithLabel.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase())
      );
    // Sort alphabetically by label if enabled
    return sortOptions ? filtered.sort((a, b) => a.label.localeCompare(b.label)) : filtered;
  }, [options, searchQuery, sortOptions]);

  // Get selected option labels
  const selectedLabels = useMemo(() => {
    return options
      .filter((opt) => selectedValues.includes(opt.value))
      .map((opt) => opt.label);
  }, [options, selectedValues]);

  const handleSelectionChange = (key: string) => {
    if (multiSelect) {
      const newValues = selectedValues.includes(key)
        ? selectedValues.filter((v) => v !== key)
        : [...selectedValues, key];
      onChange?.(newValues);
    } else {
      onChange?.(key);
      setIsOpen(false);
    }
  };

  const handleRemove = (valueToRemove: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove?.(valueToRemove);
  };

  const handleRemoveFromTrigger = (valueToRemove: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (multiSelect) {
      const newValues = selectedValues.filter((v) => v !== valueToRemove);
      onChange?.(newValues);
    } else {
      onChange?.(multiSelect ? [] : '');
    }
    onRemove?.(valueToRemove);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchQuery('');
      setCreateValue('');
      setCreateError('');
      setTagsExpanded(false);
    }
  };

  const handleCreate = () => {
    if (createValue.trim()) {
      const trimmedValue = createValue.trim();
      const normalizedValue = trimmedValue.toLowerCase();

      // Check if option already exists (case-insensitive)
      const optionExists = options.some(
        (option) => option.label.trim().toLowerCase() === normalizedValue
      );

      if (!optionExists) {
        // Create new option object
        const newOption: SelectOption = {
          value: trimmedValue,
          label: trimmedValue
        };

        // Add to options array and optionally sort alphabetically
        const updatedOptions = sortOptions
          ? [...options, newOption].sort((a, b) => a.label.localeCompare(b.label))
          : [...options, newOption];
        onOptionsChange?.(updatedOptions);

        // Call onCreate callback
        onCreate?.(trimmedValue, autoSelectCreated);

        // Auto-select if enabled
        if (autoSelectCreated) {
          if (multiSelect) {
            const newValues = [...selectedValues, trimmedValue];
            onChange?.(newValues);
          } else {
            onChange?.(trimmedValue);
            setIsOpen(false);
          }
        }

        setCreateValue('');
        setCreateError('');
      } else {
        setCreateError('This option already exists');
      }
    }
  };

  const handleCreateInputChange = (value: string) => {
    setCreateValue(value);
    if (createError) {
      setCreateError('');
    }
  };

  return (
    <div
      className={clsx(
        styles.wrapper,
        fullWidth ? styles.wrapperFullWidth : styles.wrapperDefault,
        className
      )}
      style={{
        ...(borderColor ? { '--custom-border-color': borderColor } as React.CSSProperties : {}),
      }}
      ref={wrapperRef}
    >
      {label && <Label className={styles.label}>{label}</Label>}

      <div className={styles.triggerContainer}>
        <Button
          onPress={() => !disabled && handleOpenChange(!isOpen)}
          className={clsx(
            styles.trigger,
            styles[`trigger${size.charAt(0).toUpperCase() + size.slice(1)}`],
            {
              [styles.triggerOpen]: isOpen,
              [styles.triggerError]: hasError,
              [styles.triggerDisabled]: disabled,
              [styles.triggerCustomBorder]: !!borderColor,
              [styles.triggerNoRadius]: hasNoRadius,
              [styles.triggerNoBoxShadow]: hasNoBoxShadow,
            }
          )}
          isDisabled={disabled}
        >
          <div className={styles.triggerContent}>
            {selectedValues.length > 0 ? (
              multiSelect ? (() => {
                const maxVisibleTags = 3;
                const shouldCollapse = selectedValues.length > maxVisibleTags && (isOpen || !tagsExpanded);
                const visibleValues = shouldCollapse ? selectedValues.slice(0, maxVisibleTags) : selectedValues;
                const hiddenCount = selectedValues.length - maxVisibleTags;

                return (
                  <>
                    {visibleValues.map((val) => {
                      const label = options.find(opt => opt.value === val)?.label || val;
                      return (
                        <span key={val} className={styles.selectedTag} title={label}>
                          <span className={styles.selectedTagText}>{label}</span>
                          <button
                            onClick={(e) => handleRemoveFromTrigger(val, e)}
                            className={styles.removeButton}
                            type="button"
                            aria-label={`Remove ${label}`}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                    {shouldCollapse && hiddenCount > 0 && (
                      <span
                        className={clsx(styles.selectedTag, styles.moreTag)}
                        onClick={(e) => {
                          e.stopPropagation();
                          setTagsExpanded(true);
                        }}
                        role="button"
                        tabIndex={-1}
                        aria-label={`Show ${hiddenCount} more selected items`}
                      >
                        +{hiddenCount} more
                      </span>
                    )}
                  </>
                );
              })() : (
                <span className={styles.singleValue}>
                  <span className={styles.singleValueText}>
                    {selectedLabels[0]}
                  </span>
                  {removable && (
                    <button
                      onClick={(e) => handleRemoveFromTrigger(selectedValues[0], e)}
                      className={styles.removeButton}
                      type="button"
                    >
                      ×
                    </button>
                  )}
                </span>
              )
            ) : (
              <span className={styles.placeholder}>{placeholder}</span>
            )}
          </div>
          {clearable && selectedValues.length > 0 && !disabled && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onChange?.(multiSelect ? [] : '');
                onClear?.();
              }}
              className={styles.clearButton}
              type="button"
              aria-label="Clear selection"
            >
              ×
            </button>
          )}
          <svg
            width="20"
            height="20"
            viewBox="0 0 28 28"
            fill="none"
            aria-hidden="true"
            className={clsx(styles.chevron, { [styles.chevronOpen]: isOpen })}
          >
            <path d="M22.75 10.5L14 19.25L5.25 10.5" stroke="#1A5765" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Button>

        {isOpen && (
          <div
            className={clsx(
              styles.popover,
              {
                [styles.popoverCustomBorder]: !!borderColor,
                [styles.popoverNoRadius]: hasNoRadius,
                [styles.popoverNoBoxShadow]: hasNoBoxShadow,
              }
            )}
          >
            {searchable && (
              <input
                type="search"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className={clsx(styles.searchInput, { [styles.searchInputNoRadius]: hasNoRadius })}
                autoFocus
              />
            )}

            <div
              className={clsx(
                styles.listBox,
                {
                  [styles.listBoxWithSearch]: searchable && !creatable,
                  [styles.listBoxWithCreate]: !searchable && creatable,
                  [styles.listBoxWithSearchAndCreate]: searchable && creatable,
                }
              )}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => {
                  const isSelected = selectedValues.includes(option.value);
                  const isHovered = hoveredOption === option.value;
                  const isLastElement = index === filteredOptions.length - 1;
                  return (
                    <div
                      data-test='select-option'
                      key={option.value}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!option.disabled) handleSelectionChange(option.value);
                      }}
                      className={clsx(
                        styles.listItem,
                        {
                          [styles.listItemParentSearchable]: searchable,
                          [styles.listItemSelected]: isSelected,
                          [styles.listItemDisabled]: option.disabled,
                          [styles.listItemLast]: isLastElement && !creatable,
                          [styles.listItemLastRounded]: isSelected && isLastElement && !creatable,
                        }
                      )}
                      onMouseEnter={() => !option.disabled && setHoveredOption(option.value)}
                      onMouseLeave={() => setHoveredOption(null)}
                    >
                      <span className={styles.listItemContent}>
                        {multiSelect && (
                          <CustomCheckbox
                            isSelected={isSelected}
                            isDisabled={option.disabled}
                            noInteraction
                          />
                        )}
                        <span className={styles.listItemLabel} title={option.label}>{option.label}</span>
                      </span>
                      {removable && isHovered && (
                        <button
                          onClick={(e) => handleRemove(option.value, e)}
                          className={styles.removeButton}
                          type="button"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className={clsx(styles.listItem, styles.noOptions)}>
                  {noOptionsMessage}
                </div>
              )}
            </div>

            {creatable && (
              <div className={styles.createSection}>
                <div className={styles.createInputWrapper}>
                  <CustomTextInput
                    placeholder={createPlaceholder}
                    value={createValue}
                    onChange={handleCreateInputChange}
                    maxLength={createMaxLength}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const trimmedValue = createValue.trim();
                        const normalizedValue = trimmedValue.toLowerCase();
                        const optionExists = options.some(
                          (option) => option.label.trim().toLowerCase() === normalizedValue
                        );
                        if (!trimmedValue || optionExists) return;
                        handleCreate();
                      }
                    }}
                    error={createError}
                    fullWidth
                    size={size}
                    borderColor={borderColor}
                    hasNoRadius={hasNoRadius}
                    hasNoBoxShadow={hasNoBoxShadow}
                  />
                  <CustomButton
                    onPress={handleCreate}
                    isDisabled={!createValue.trim() || (() => {
                      const trimmedValue = createValue.trim();
                      const normalizedValue = trimmedValue.toLowerCase();
                      return options.some(
                        (option) => option.label.trim().toLowerCase() === normalizedValue
                      );
                    })()}
                    size={size}
                    fullWidth={true}
                    variant="primary"
                    hasNoRadius={hasNoRadius}
                    hasNoBoxShadow={hasNoBoxShadow}
                  >
                    {createButtonText}
                  </CustomButton>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {(error || helperText) && (
        <div className={clsx(styles.helper, { [styles.helperError]: hasError })}>
          {error || helperText}
        </div>
      )}
    </div>
  );
};

CustomSelect.displayName = 'ProdiktCustomSelect';
